// =============================================================================
// Language edition of the set (the Chinese set for the contractors, 2026-10-26).
// The sheets keep their English source strings; at render time every string
// the kit draws (and every workbook cell) is looked up in a dictionary keyed
// by its TEMPLATE: the string with its numbers, dimensions and equipment tags
// swapped for {0}, {1} ... so "CU-07 at 2'-1\" from P1" and "CU-14 at 3'-0\"
// from P2" share one entry. The tokens go back into the translation.
//
// The launcher (scripts/set.mjs --lang zh, scripts/bom.mjs --lang zh) sets
// globalThis.__SHEET_LANG = { lang, dict, fonts, dump } before the bundle is
// imported; the browser viewer never sets it and stays English.
// =============================================================================
export type Lang = "en" | "zh";
export interface LangSetup {
  lang: Lang;
  /** template -> translated template */
  dict?: Record<string, string>;
  /** base64 TTF subsets for the CJK face */
  fonts?: { normal: string; bold?: string };
  /** when set, every string the kit draws is pushed here (the English string dump) */
  dump?: string[];
  /** filled by the kit: templates with no translation (template -> an example string) */
  missing?: Record<string, string>;
}

const G = globalThis as unknown as { __SHEET_LANG?: LangSetup };
const setup: LangSetup = G.__SHEET_LANG ?? { lang: "en" };
if (G.__SHEET_LANG) { setup.missing = setup.missing ?? {}; }

export const lang = (): Lang => setup.lang;
export const fonts = () => setup.fonts;
/** true when a launcher asked for an edition or a string dump (the browser viewer never does) */
export const edition = () => setup.lang !== "en" || !!setup.dump;

// an equipment / sheet / circuit tag (CU-07, BR1.1/9, SW-GF-012, AP-A3-HOOD, E151, IP44, DALI-2),
// or a number with its feet-inch marks, fraction, % or unit mark (2'-1", 0.75, 1,500, 1/2, 84 %)
const TOKEN = /\b[A-Z]{1,4}(?:-[A-Z]{1,4})*-?\d+[A-Z0-9]*(?:[./-][A-Z0-9]+)*\b|[-+]?\d[\d,]*(?:\.\d+)?(?:'-\d+(?:\s\d+\/\d+)?"|\/\d+|'|"|%)?/g;

/** the template of a string and the tokens it carried; poss[i]: token i is a number whose "'" is the apostrophe of a following "'s"
 *  ("Apt 9's" - TOKEN takes "9'" as feet), not a foot mark */
export function templ(s: string): { key: string; tokens: string[]; poss: boolean[] } {
  const tokens: string[] = [], poss: boolean[] = [];
  const key = s.replace(TOKEN, (m: string, at: number) => { tokens.push(m); poss.push(/^\d[\d,.]*'$/.test(m) && /^s(?![A-Za-z])/.test(s.slice(at + m.length))); return `{${tokens.length - 1}}`; });
  return { key, tokens, poss };
}

/** what follows a placeholder in a translation when it goes on in Chinese right after it: a CJK character or a full-width mark (after a
 *  space it does not: "主卫 {0} 衣帽间 {1}" is a list the English commas still separate) - or, after a space, a particle that ties
 *  the number to what follows ("走廊 {0} 的 {1} 隔墙上") */
const ZH_NEXT = /^(?:[　-鿿豈-﫿＀-￯‘’“”—]| (?:的|内|上方|下方|处))/;
/** the translation with the tokens back in. A number's trailing comma ("91," - TOKEN takes it whole) is the English text's own
 *  separator: where the translation goes on in Chinese after the placeholder (its own "，" or "、", or a word) it is left out, so a
 *  line never prints "91,，" (2026-11-03); the apostrophe of a possessive "'s" (poss) is left
 *  out too, so "Apt 9's" never prints "9'号公寓" as if 9 feet; whole: as the issued edition (2026-10-27) printed it, every token whole */
const fill = (t: string, tokens: string[], whole = false, poss: boolean[] = []) => t.replace(/\{(\d+)\}/g, (m: string, i: string, at: number) => {
  const v = tokens[Number(i)]; if (v === undefined) return m;
  if (whole) return v;
  if (poss[Number(i)]) return v.slice(0, -1);
  return v.endsWith(",") && ZH_NEXT.test(t.slice(at + m.length)) ? v.replace(/,+$/, "") : v;
});
const placeholders = (t: string) => (t.match(/\{\d+\}/g) ?? []).sort().join(" ");
/** a string this edition draws other than the issued edition did (a token's comma left out: fill) -> as the issued edition drew it,
 *  in capitals too (the revision compare matches a text against the issued record by the issued form as well: kit.ts revGroup) */
const issued = new Map<string, string>(), issuedList: [string, string][] = [];
export const asIssued = (s: string): string | undefined => issued.get(s);
/** those (drawn, issued) pairs, the n-th on (the kit reads the places a comma was left out from them) */
export const issuedFrom = (n: number): [string, string][] => issuedList.slice(n);

/** the string as the edition draws it: itself in English; its translation in Chinese (English, logged, where none exists) */
export function tr(s: string): string {
  if (!s.trim() || hasCJK(s)) return s;
  if (setup.dump) setup.dump.push(s);
  if (setup.lang === "en" || !setup.dict) return s;
  const { key, tokens, poss } = templ(s);
  const hit = setup.dict[key];
  if (hit !== undefined && placeholders(hit) === placeholders(key)) {
    const out = fill(hit, tokens, false, poss), was = fill(hit, tokens, true);
    if (was !== out && !issued.has(out)) for (const [o, w] of new Map([[out, was], [out.toUpperCase(), was.toUpperCase()]])) { issued.set(o, w); issuedList.push([o, w]); }
    return out;
  }
  if (!/[A-Za-z]{2,}/.test(key)) return s; // nothing to translate: tags and numbers only
  if (setup.missing && !(key in setup.missing)) setup.missing[key] = s;
  return s;
}

export const hasCJK = (s: string) => /[　-鿿豈-﫿＀-￯]/.test(s);

/** every string cell of a built workbook translated (and dumped), sheet names included */
export async function translateWorkbook(ExcelJS: any, buf: ArrayBuffer | Uint8Array): Promise<ArrayBuffer> {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf as any);
  for (const ws of wb.worksheets) {
    ws.name = tr(ws.name).slice(0, 31);
    ws.eachRow({ includeEmpty: false }, (row: any) => {
      row.eachCell({ includeEmpty: false }, (cell: any) => {
        const v = cell.value;
        if (typeof v === "string") cell.value = tr(v);
        else if (v && typeof v === "object" && Array.isArray(v.richText)) cell.value = { richText: v.richText.map((r: any) => ({ ...r, text: tr(String(r.text)) })) };
      });
    });
  }
  return wb.xlsx.writeBuffer();
}
